<div class="subscription-dialog-shell">
  <!-- Dialog Header -->
  <div class="dialog-header">
    <div class="header-main">
      <div class="header-icon-wrap">
        <mat-icon>card_membership</mat-icon>
      </div>
      <div>
        <h2>Subscription Management</h2>
        <p class="subtitle">Sprinta Annual Project Licensing & Seat Capacity</p>
      </div>
    </div>
    <div class="header-right">
      <span class="status-badge" [ngClass]="subscription.status">
        <mat-icon>{{
          subscription.status === 'active' ? 'verified' :
          subscription.status === 'trial' ? 'schedule' :
          subscription.status === 'expiring_soon' ? 'warning' :
          subscription.status === 'grace_period' ? 'error_outline' : 'lock'
        }}</mat-icon>
        <span>{{
          subscription.status === 'active' ? 'Active Subscription' :
          subscription.status === 'trial' ? 'Free Trial (15 Days)' :
          subscription.status === 'expiring_soon' ? 'Renewal Required' :
          subscription.status === 'grace_period' ? 'Grace Period (15 Days)' : 'Account Blocked'
        }}</span>
      </span>
      <button mat-icon-button mat-dialog-close (click)="close()" aria-label="Close dialog">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Lifecycle Notification Alerts -->
  @if (subscription.status === 'expiring_soon') {
    <div class="notice-banner warn">
      <mat-icon>warning</mat-icon>
      <div>
        <strong>Subscription Renewal Notice:</strong> Your subscription expires in {{ subscription.daysRemaining }} days. Renew now to maintain uninterrupted access.
      </div>
    </div>
  }
  @if (subscription.status === 'grace_period') {
    <div class="notice-banner urgent">
      <mat-icon>error</mat-icon>
      <div>
        <strong>Grace Period Active:</strong> Subscription expired {{ 15 - subscription.daysRemaining }} days ago. You have {{ subscription.daysRemaining }} days before account mutations are blocked.
      </div>
    </div>
  }
  @if (subscription.status === 'blocked') {
    <div class="notice-banner blocked">
      <mat-icon>lock</mat-icon>
      <div>
        <strong>Account Blocked:</strong> Grace period expired. Mutating actions (adding members, creating sprints, moving tasks) are restricted until renewal.
      </div>
    </div>
  }

  <!-- Dialog Tab Navigation -->
  <mat-tab-group [(selectedIndex)]="selectedTabIndex" animationDuration="200ms">

    <!-- TAB 1: Plan & Seats -->
    <mat-tab label="Plan & Seat Allocation">
      <div class="tab-body">
        <!-- Pricing Banner -->
        <div class="pricing-hero">
          <div class="price-side">
            <div class="price-figure">
              <span class="currency">A$</span>
              <span class="amount">{{ pricing.monthlyPriceAud }}</span>
              <span class="cadence">/ month</span>
            </div>
            <span class="billing-note">{{ pricing.display.billingSubtitle }}</span>
          </div>

          <div class="unit-selector">
            <label>Subscription Units:</label>
            <div class="stepper-controls">
              <button mat-icon-button (click)="adjustUnits(-1)" [disabled]="selectedUnits <= 1 || !isOwner">
                <mat-icon>remove</mat-icon>
              </button>
              <span class="units-value">{{ selectedUnits }}</span>
              <button mat-icon-button (click)="adjustUnits(1)" [disabled]="selectedUnits >= 50 || !isOwner">
                <mat-icon>add</mat-icon>
              </button>
            </div>
            <div class="total-annual">
              = <strong>{{ totalAnnualPrice | currency:'AUD':'symbol':'1.0-0' }}</strong> / year
            </div>
          </div>
        </div>

        <!-- Seats Recommendation Box -->
        <div class="seat-recommendation-box">
          <div class="rec-header">
            <mat-icon>group</mat-icon>
            <strong>Current Team Seat Utilization</strong>
          </div>
          <div class="rec-content">
            <p>
              Your project currently has <strong>{{ seatUsage.ownersUsed }} Owner</strong>, 
              <strong>{{ seatUsage.developersUsed }} Developers</strong>, and 
              <strong>{{ seatUsage.stakeholdersUsed }} Stakeholders</strong> across all teams.
            </p>
            @if (selectedUnits < seatUsage.unitsRequired) {
              <div class="recommendation-badge warn">
                <mat-icon>warning</mat-icon>
                <span>Recommendation: Increase to at least {{ seatUsage.unitsRequired }} units to cover current members</span>
              </div>
            } @else if (selectedUnits === seatUsage.unitsRequired) {
              <div class="recommendation-badge success">
                <mat-icon>check_circle</mat-icon>
                <span>Perfect fit: Exactly covers all {{ seatUsage.developersUsed }} developers and {{ seatUsage.stakeholdersUsed }} stakeholders</span>
              </div>
            } @else {
              <div class="recommendation-badge info">
                <mat-icon>info</mat-icon>
                <span>Includes spare capacity for growth</span>
              </div>
            }
          </div>
        </div>

        <!-- Visual Seat Breakdown -->
        <h4 class="section-title">Seat Capacity Allocation</h4>
        <div class="seats-grid">
          <!-- Owners Card -->
          <div class="seat-card">
            <div class="seat-card-header">
              <div class="seat-title">
                <mat-icon>admin_panel_settings</mat-icon>
                <strong>Project Owner</strong>
              </div>
              <span class="seat-count">{{ seatUsage.ownersUsed }} / {{ ownerCapacity }}</span>
            </div>
            <div class="progress-bar-wrap">
              <div class="progress-fill" [style.width.%]="(seatUsage.ownersUsed / ownerCapacity) * 100"></div>
            </div>
            <small class="seat-desc">Covers yourself and administrators (1 seat per unit)</small>
          </div>

          <!-- Developers Card -->
          <div class="seat-card" [class.over-capacity]="seatUsage.developersUsed > developerCapacity">
            <div class="seat-card-header">
              <div class="seat-title">
                <mat-icon>code</mat-icon>
                <strong>Developers & Contributors</strong>
              </div>
              <span class="seat-count" [class.warn-text]="seatUsage.developersUsed > developerCapacity">
                {{ seatUsage.developersUsed }} / {{ developerCapacity }}
              </span>
            </div>
            <div class="progress-bar-wrap">
              <div class="progress-fill" 
                [class.warn]="seatUsage.developersUsed > developerCapacity" 
                [style.width.%]="Math.min(100, (seatUsage.developersUsed / developerCapacity) * 100)">
              </div>
            </div>
            <small class="seat-desc">
              @if (seatUsage.developersUsed > developerCapacity) {
                <span class="error-text">Capacity exceeded by {{ seatUsage.developersUsed - developerCapacity }}. Add 1 more unit.</span>
              } @else {
                <span>{{ developerCapacity - seatUsage.developersUsed }} seats available across all teams (5 per unit)</span>
              }
            </small>
          </div>

          <!-- Stakeholders Card -->
          <div class="seat-card" [class.over-capacity]="seatUsage.stakeholdersUsed > stakeholderCapacity">
            <div class="seat-card-header">
              <div class="seat-title">
                <mat-icon>visibility</mat-icon>
                <strong>Stakeholders (Internal & External)</strong>
              </div>
              <span class="seat-count" [class.warn-text]="seatUsage.stakeholdersUsed > stakeholderCapacity">
                {{ seatUsage.stakeholdersUsed }} / {{ stakeholderCapacity }}
              </span>
            </div>
            <div class="progress-bar-wrap">
              <div class="progress-fill" 
                [class.warn]="seatUsage.stakeholdersUsed > stakeholderCapacity" 
                [style.width.%]="Math.min(100, (seatUsage.stakeholdersUsed / stakeholderCapacity) * 100)">
              </div>
            </div>
            <small class="seat-desc">
              @if (seatUsage.stakeholdersUsed > stakeholderCapacity) {
                <span class="error-text">Capacity exceeded. Add 1 more unit.</span>
              } @else {
                <span>{{ stakeholderCapacity - seatUsage.stakeholdersUsed }} seats available for roadmap viewers (3 per unit)</span>
              }
            </small>
          </div>
        </div>

        <!-- Feature Transparency Note -->
        <div class="feature-note">
          <mat-icon>check</mat-icon>
          <span>All Sprinta features are unlocked for your team. Developers and Stakeholders do not pay anything.</span>
        </div>

        <!-- Actions -->
        @if (isOwner) {
          <div class="tab-actions">
            <button mat-flat-button color="primary" class="proceed-btn" (click)="goToPayment()">
              <mat-icon>credit_card</mat-icon>
              <span>{{ subscription.status === 'trial' ? 'Upgrade to Annual Plan' : 'Proceed to Payment' }} ({{ totalAnnualPrice | currency:'AUD':'symbol':'1.0-0' }}/yr)</span>
            </button>
          </div>
        } @else {
          <div class="non-owner-notice">
            <mat-icon>lock</mat-icon>
            <span>You are viewing in read-only mode. Only the Project Owner can purchase or modify subscription units.</span>
          </div>
        }
      </div>
    </mat-tab>

    <!-- TAB 2: Payment -->
    <mat-tab label="Payment">
      <div class="tab-body">
        @if (directPostResult && directPostResult.confirmation) {
          <!-- Payment Success Receipt matching Reference template-receipt.html -->
          <div class="receipt-card">
            <div class="receipt-icon">
              <mat-icon>check_circle</mat-icon>
            </div>
            <h3>Payment Approved</h3>
            <p class="receipt-sub">Processed securely via NAB Transact Direct Post v2</p>

            <div class="receipt-table">
              <div class="receipt-row">
                <span>Payment Reference:</span>
                <strong>{{ directPostResult.details?.custom?.refid || prepareData?.referenceId }}</strong>
              </div>
              <div class="receipt-row">
                <span>NAB Transaction ID:</span>
                <strong>{{ directPostResult.details?.transactionId }}</strong>
              </div>
              <div class="receipt-row">
                <span>Amount Paid:</span>
                <strong>{{ totalAnnualPrice | currency:'AUD':'symbol':'1.2-2' }} AUD</strong>
              </div>
              <div class="receipt-row">
                <span>Credit Card:</span>
                <strong>{{ directPostResult.details?.payment?.pan || ('•••• •••• •••• ' + subscription.paymentMethod?.last4) }}</strong>
              </div>
              <div class="receipt-row">
                <span>Name on Credit Card:</span>
                <strong>(Hidden)</strong>
              </div>
              <div class="receipt-row">
                <span>Capacity Covered:</span>
                <strong>{{ selectedUnits }} Unit{{ selectedUnits > 1 ? 's' : '' }} ({{ ownerCapacity }} Owner, {{ developerCapacity }} Devs, {{ stakeholderCapacity }} Stakeholders)</strong>
              </div>
              <div class="receipt-row">
                <span>Subscription Validity:</span>
                <strong>365 days (Valid until {{ subscription.currentPeriodEnd | date:'mediumDate' }})</strong>
              </div>
            </div>

            <div class="receipt-actions">
              <button mat-flat-button color="primary" (click)="close()">
                <mat-icon>done</mat-icon> Return to Sprinta
              </button>
            </div>
          </div>
        } @else {
          <!-- Direct Post Order & Fingerprint Status -->
          <div class="order-summary-strip">
            <div>
              <span>Billing Summary:</span>
              <strong>{{ selectedUnits }} Unit{{ selectedUnits > 1 ? 's' : '' }} &times; {{ pricing.display.priceTag }} ({{ pricing.annualPriceAud | currency:'AUD':'symbol':'1.0-0' }} AUD/yr)</strong>
            </div>
            <div class="order-price">
              <span>Total Due:</span>
              <strong>{{ totalAnnualPrice | currency:'AUD':'symbol':'1.2-2' }} AUD</strong>
              <small>Billed annually (12 months)</small>
            </div>
          </div>

          <!-- Direct Post Fingerprint Security Strip -->
          
          <!-- 501 Deferred Payment Notice in Live API Mode -->
          @if (apiConfig.isLiveMode) {
            <div class="deferred-payment-banner">
              <mat-icon>info</mat-icon>
              <div>
                <strong>Payment Integration Pending (Initial Release):</strong>
                <span>The backend API has payment processing deferred (501 PAYMENT_PROVIDER_NOT_CONFIGURED). Subscriptions are active in launch mode.</span>
              </div>
            </div>
          }

          <div class="fingerprint-badge">
            <mat-icon>lock</mat-icon>
            <span>
              <strong>NAB Transact Direct Post v2:</strong> Session HMAC fingerprint signed
              <small class="fp-ref">Ref: {{ prepareData?.referenceId || 'Connecting...' }}</small>
              &bull; Auto-refreshes every 4.5 minutes
            </span>
          </div>

          <!-- Test Card Autofill Helper -->
          <div class="test-helper-strip">
            <span class="test-helper-label"><mat-icon>credit_card</mat-icon> NAB Transact Test Cards:</span>
            <button mat-stroked-button (click)="fillTestCard('approved')">Approved Card</button>
            <button mat-stroked-button (click)="fillTestCard('insufficient')">Insufficient Funds</button>
            <button mat-stroked-button (click)="fillTestCard('expired')">Expired Card</button>
          </div>

          @if (paymentError) {
            <div class="error-banner">
              <mat-icon>error_outline</mat-icon>
              <span>{{ paymentError }}</span>
            </div>
          }

          <div class="card-form-grid">
            <!-- Card Number -->
            <mat-form-field appearance="outline" class="card-number-field">
              <mat-label>Credit Card Number</mat-label>
              <input matInput [value]="cardNumber" (input)="onCardNumberInput($event)" placeholder="4564 0000 0000 0001" maxlength="19">
              <span matSuffix class="card-brand-chip">
                <mat-icon>credit_card</mat-icon>
                <span>{{ detectedCardBrand }}</span>
              </span>
            </mat-form-field>

            <!-- Cardholder Name -->
            <mat-form-field appearance="outline" class="full-field">
              <mat-label>Name on Credit Card</mat-label>
              <input matInput [(ngModel)]="cardholderName" placeholder="e.g. Mark Taylor">
              <mat-icon matPrefix>person</mat-icon>
            </mat-form-field>

            <!-- Expiry & CVV Row -->
            <div class="form-row-3">
              <mat-form-field appearance="outline">
                <mat-label>Expiry Month</mat-label>
                <mat-select [(ngModel)]="expiryMonth">
                  @for (m of months; track m) {
                    <mat-option [value]="m">{{ m }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Expiry Year</mat-label>
                <mat-select [(ngModel)]="expiryYear">
                  @for (y of years; track y) {
                    <mat-option [value]="y">{{ y }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>CCV / CVC</mat-label>
                <input matInput [(ngModel)]="cvv" placeholder="123" maxlength="4">
                <mat-icon matSuffix matTooltip="3 digits on back of Visa/Mastercard, 4 digits on front of Amex">help_outline</mat-icon>
              </mat-form-field>
            </div>

            <!-- Postcode -->
            <mat-form-field appearance="outline" class="postcode-field">
              <mat-label>Billing Postcode</mat-label>
              <input matInput [(ngModel)]="postcode" placeholder="3000">
            </mat-form-field>
          </div>

          <!-- Statutory Compliance Checkbox matching Customer reference template-pay.html -->
          <div class="nab-compliance-box">
            <mat-checkbox [(ngModel)]="agreePayments" required>
              All Credit Card details will be processed securely by <strong>NAB Transact</strong>. 
              These details are not available to Sprinta nor are they stored by us or known by us at any time. 
              All payments are in Australian Dollars (AUD) for the amount shown above.
            </mat-checkbox>
          </div>

          <div class="payment-actions">
            <button mat-button (click)="selectedTabIndex = 0">Back to Seats</button>
            <button mat-flat-button color="primary" class="pay-submit-btn" 
              [disabled]="isProcessing || !agreePayments || !cardNumber.trim()" 
              (click)="processPayment()">
              <mat-icon>{{ isProcessing ? 'sync' : 'lock' }}</mat-icon>
              <span>{{ isProcessing ? 'Processing via NAB Transact...' : ('Pay ' + (totalAnnualPrice | currency:'AUD':'symbol':'1.2-2') + ' AUD') }}</span>
            </button>
          </div>
        }
      </div>
    </mat-tab>

    <!-- TAB 3: Invoices & Receipts -->
    <mat-tab label="Invoices & History">
      <div class="tab-body">
        @if (invoices.length > 0) {
          <div class="invoices-list">
            @for (inv of invoices; track inv.invoiceNumber) {
              <div class="invoice-item">
                <div class="inv-main">
                  <div class="inv-title">
                    <strong>{{ inv.invoiceNumber }}</strong>
                    <span class="paid-chip">Paid</span>
                  </div>
                  <span class="inv-desc">{{ inv.description }}</span>
                  <small class="inv-meta">NAB Receipt: {{ inv.nabReceipt }} &bull; Paid on {{ inv.date | date:'mediumDate' }} &bull; Card: &bull;&bull;&bull;&bull; {{ inv.cardLast4 }}</small>
                </div>
                <div class="inv-amount">
                  <strong>{{ inv.amount | currency:'AUD':'symbol':'1.2-2' }}</strong>
                  <small>AUD</small>
                </div>
              </div>
            }
          </div>
        } @else {
          <div class="empty-invoices">
            <mat-icon>receipt_long</mat-icon>
            <h4>No payment history yet</h4>
            <p>Once your annual subscription is purchased, tax receipts and NAB Transact confirmation statements will appear here.</p>
          </div>
        }
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
